/**
 * `evidence-queue` (W45-F5-S1, #1476) — an IndexedDB-backed, blob-safe queue
 * for offline evidence capture.
 *
 * Field workers on spotty connectivity capture evidence (photo / document +
 * a note) bound to a building. Rather than failing the upload when offline,
 * the capture flow enqueues the item here. The sync worker
 * (`sync-worker.ts`) drains the queue to the real evidence-upload endpoint
 * when connectivity returns.
 *
 * Storage: one IndexedDB key per item (`evidence-queue:<id>`) plus an index
 * key (`evidence-queue:index`) holding the ordered id list. Blobs are stored
 * natively — IndexedDB structured-clones them, so no base64 round-trip. The
 * underlying store is the swappable {@link IdbLike} seam so tests inject an
 * in-memory backend (see `idb-store.ts`).
 *
 * Status lifecycle: `pending → syncing → synced` (then removed) or
 * `pending → syncing → failed` (kept for manual retry). `failed` items are
 * re-armed to `pending` by {@link markFailed}'s caller via {@link enqueueRetry}
 * semantics — see the sync worker.
 */

import { createIdbStore, type IdbLike } from './idb-store';

export type EvidenceQueueStatus = 'pending' | 'syncing' | 'synced' | 'failed';

/**
 * A single queued evidence capture. `blobs` are the captured files
 * (photos/documents); `note` + `buildingId` mirror the upload payload the
 * sync worker POSTs to the evidence endpoint.
 */
export interface EvidenceQueueItem {
  readonly id: string;
  /** Epoch ms the capture happened — drives FIFO drain order. */
  readonly capturedAt: number;
  /** Captured files. At least one; native Blob storage, no base64. */
  readonly blobs: Blob[];
  /** Original filenames, parallel to `blobs` (Blobs drop `File.name`). */
  readonly filenames: string[];
  readonly note: string;
  /** Building/asset the evidence attaches to (the upload route param). */
  readonly buildingId: string;
  /** Optional GPS fix (W45-F5-S2 wires this in; carried opaquely here). */
  readonly lat?: number;
  readonly lng?: number;
  status: EvidenceQueueStatus;
  /** Sync attempt count — drives exponential backoff in the sync worker. */
  attempts: number;
  /** Last failure message, surfaced in the queue-status UI. */
  lastError?: string;
}

const KEY_PREFIX = 'evidence-queue:';
const INDEX_KEY = `${KEY_PREFIX}index`;

function itemKey(id: string): string {
  return `${KEY_PREFIX}${id}`;
}

function newId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The offline evidence queue. A thin, persistent CRUD layer over an
 * {@link IdbLike} store. All higher-level concerns (online detection,
 * backoff, the actual POST) live in the sync worker — this class only owns
 * durable persistence + ordering.
 */
export class EvidenceQueue {
  private readonly store: IdbLike;

  constructor(store: IdbLike = createIdbStore()) {
    this.store = store;
  }

  private async readIndex(): Promise<string[]> {
    const index = await this.store.get<string[]>(INDEX_KEY);
    return Array.isArray(index) ? index : [];
  }

  private async writeIndex(ids: string[]): Promise<void> {
    await this.store.set<string[]>(INDEX_KEY, ids);
  }

  /**
   * Persists a new capture as a `pending` item and returns it. Generates the
   * id + `capturedAt` when not supplied so callers can stay terse.
   */
  async enqueue(input: {
    blobs: Blob[];
    filenames: string[];
    note: string;
    buildingId: string;
    lat?: number;
    lng?: number;
    capturedAt?: number;
    id?: string;
  }): Promise<EvidenceQueueItem> {
    const item: EvidenceQueueItem = {
      id: input.id ?? newId(),
      capturedAt: input.capturedAt ?? Date.now(),
      blobs: input.blobs,
      filenames: input.filenames,
      note: input.note,
      buildingId: input.buildingId,
      lat: input.lat,
      lng: input.lng,
      status: 'pending',
      attempts: 0,
    };
    await this.store.set<EvidenceQueueItem>(itemKey(item.id), item);
    const index = await this.readIndex();
    if (!index.includes(item.id)) {
      index.push(item.id);
      await this.writeIndex(index);
    }
    return item;
  }

  /** All queued items, oldest first (FIFO drain order). */
  async list(): Promise<EvidenceQueueItem[]> {
    const ids = await this.readIndex();
    const items: EvidenceQueueItem[] = [];
    for (const id of ids) {
      const item = await this.store.get<EvidenceQueueItem>(itemKey(id));
      if (item) items.push(item);
    }
    return items.sort((a, b) => a.capturedAt - b.capturedAt);
  }

  /** A single item by id, or `undefined` if absent. */
  async get(id: string): Promise<EvidenceQueueItem | undefined> {
    return this.store.get<EvidenceQueueItem>(itemKey(id));
  }

  /** Removes an item from the store and the ordered index. */
  async dequeue(id: string): Promise<void> {
    await this.store.delete(itemKey(id));
    const index = await this.readIndex();
    const next = index.filter((existing) => existing !== id);
    if (next.length !== index.length) await this.writeIndex(next);
  }

  /**
   * Marks an item `synced` then removes it — a synced capture is durable on
   * the server, so it leaves the local queue. No-op if the id is gone.
   */
  async markSynced(id: string): Promise<void> {
    await this.dequeue(id);
  }

  /** Flips an item to `syncing` (drain start). No-op if absent. */
  async markSyncing(id: string): Promise<void> {
    await this.patch(id, (item) => {
      item.status = 'syncing';
      item.attempts += 1;
    });
  }

  /**
   * Flips an item to `failed` with the error message, leaving it in the queue
   * for manual or automatic retry. No-op if absent.
   */
  async markFailed(id: string, error: string): Promise<void> {
    await this.patch(id, (item) => {
      item.status = 'failed';
      item.lastError = error;
    });
  }

  /** Re-arms a `failed` item to `pending` for another drain pass. */
  async markPending(id: string): Promise<void> {
    await this.patch(id, (item) => {
      item.status = 'pending';
      item.lastError = undefined;
    });
  }

  /** Removes every item + the index. Used by tests and "clear all". */
  async clear(): Promise<void> {
    const ids = await this.readIndex();
    for (const id of ids) {
      await this.store.delete(itemKey(id));
    }
    await this.store.delete(INDEX_KEY);
  }

  private async patch(
    id: string,
    mutate: (item: EvidenceQueueItem) => void,
  ): Promise<void> {
    const item = await this.store.get<EvidenceQueueItem>(itemKey(id));
    if (!item) return;
    mutate(item);
    await this.store.set<EvidenceQueueItem>(itemKey(id), item);
  }
}

/**
 * Process-wide singleton so the capture flow, sync worker, and topbar
 * indicator all share the same persistent queue.
 */
let singleton: EvidenceQueue | undefined;

export function getEvidenceQueue(): EvidenceQueue {
  if (!singleton) singleton = new EvidenceQueue();
  return singleton;
}

/** Test seam — lets specs inject a queue backed by an in-memory store. */
export function setEvidenceQueueForTests(queue: EvidenceQueue | undefined): void {
  singleton = queue;
}
